import { type FC, useEffect, useState } from 'react'
import { KibDateField, type KibDateFieldProps } from '@chewy/kib-calendar-react'
import moment from 'moment-timezone'
import { useDebouncedCallback } from 'use-debounce'

import '@chewy/kib-calendar-styles/dist/kib-calendar.css'

export interface RhapKibDatePickerProps extends Omit<
  KibDateFieldProps,
  'value' | 'onChange'
> {
  /**
   * Whether the date picker is disabled (optional)
   */
  disabled?: boolean
  /**
   * Date input format string (default: "MM/dd/yyyy").
   * Used for validation of keyboard input.
   */
  format?: 'MM/dd/yyyy' | 'dd/MM/yyyy' | 'MM/yy'
  /**
   * Callback when the date changes, returns ISO string in business timezone
   */
  onChange: (isoDate: string | null) => void
  /**
   * Business timezone for date conversion
   */
  timezone: string
  /**
   * The date value as an ISO string in business timezone
   */
  value: string | null | undefined
}

/**
 * Checks if raw keyboard input should be rejected (invalid format).
 * Used to skip processing incomplete or incorrectly formatted dates (e.g., 2-digit years).
 */
export const isInputInvalid = (
  rawInput: string | undefined,
  inputFormat: string,
): boolean => {
  const pattern = inputFormat.toUpperCase()
  return Boolean(rawInput && !moment(rawInput, pattern, true).isValid())
}

/**
 * Timezone-aware wrapper for KibDatepicker that handles conversion between
 * business timezone ISO strings and local timezone Date objects for display.
 */
export const RhapKibDatePicker: FC<RhapKibDatePickerProps> = ({
  disabled,
  value,
  onChange,
  timezone,
  format = 'MM/dd/yyyy',
  placeholder = 'mm/dd/yyyy',
  ...props
}) => {
  const parsed = value ? moment.tz(value, timezone) : null
  const timezoneValue = parsed?.isValid()
    ? parsed.format(format.toUpperCase())
    : ''
  const [localFormattedDate, setLocalFormattedDate] =
    useState<string>(timezoneValue)

  // Keep in sync if value changes externally (e.g., form reset) - update local formatted date for display
  useEffect(() => {
    setLocalFormattedDate(timezoneValue)
  }, [timezoneValue])

  const handleDebouncedChange = useDebouncedCallback((newValue: string) => {
    if (!newValue) {
      onChange(null)
      return
    }

    // Skip processing if user is still typing or entered incomplete/invalid format
    if (isInputInvalid(newValue, format)) {
      return
    }

    // Parse the user input in the specified format, then set it to the business timezone
    // Normalize format to uppercase for moment.js (MM/DD/YYYY)
    const momentFormat = format.toUpperCase()
    const parsedMoment = moment(newValue, momentFormat, true)

    if (!parsedMoment.isValid()) {
      onChange(null)
      return
    }

    const normalizedValue = moment
      .tz(
        {
          year: parsedMoment.year(),
          month: parsedMoment.month(),
          date: parsedMoment.date(),
        },
        timezone,
      )
      .toISOString()
    onChange(normalizedValue)
  }, 500)

  return (
    <KibDateField
      disabled={disabled}
      format={format}
      help=""
      placeholder={placeholder}
      showCalendar={!disabled}
      {...props}
      value={localFormattedDate}
      onChange={(e) => {
        const newValue = e.target.value
        setLocalFormattedDate(newValue)
        handleDebouncedChange(newValue)
      }}
    />
  )
}
